import {action, computed, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {PlayerStatus, RoundStatus, TeamRegion} from "data/enums";
import {chain} from "lodash";

export interface IPlayer {
	id: number;
	fullName: string;
	firstName: string;
	lastName: string;
	status: PlayerStatus;
	country: string;
	team: TeamRegion;
	worldRanking: number | null;
	stats: IPlayerStats;
	isTop: boolean;
	fantasy: Record<number, IFantasyData>;
}

export interface IFantasyData {
	points: number;
	holes_win: number;
	tie_match: number;
	win_match: number;
	lost_match: number;
	holes_lost: number;
	holes_3_row: number;
	hole_in_one: number;
	fairways_hit: number;
	green_in_reg: number;
	holes_halved: number;
	no_holes_lost: number;
	unplayed_holes: number;
	result: string;
	matchStatus: RoundStatus;
	holeNo: string;
	teamStanding: string;
}

export interface IRecords {
	wins: number;
	losses: number;
	draws: number;
}

export interface IPlayerStats {
	RCAppearances: number;
	RCEURank: number;
	RCEUWorldRank: number;
	RCUSRank: number;
	allTimeRecords: IRecords;
	seasonRecords: IRecords;
	avgPoints: number;
	// pickedBy: number;
	fantasyPickedBy: Record<number, number>;
}

export interface IPlayersStore {
	get getIsLoading(): boolean;
	get list(): IPlayer[];
	get availablePlayersList(): IPlayer[];
	getPlayerById(playerId: number): IPlayer | undefined;
	fetchPlayers(): Promise<void>;
}

@injectable()
export class PlayersStore implements IPlayersStore {
	@observable private _isLoading: boolean = false;

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@observable private _list: IPlayer[] = [];

	get list() {
		return this._list;
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	getPlayerById(playerId: number): IPlayer | undefined {
		return this._list.find((player) => player.id === playerId);
	}

	@computed get availablePlayersList() {
		return chain(this._list)
			.filter((it) => it.status === PlayerStatus.Available)
			.orderBy("worldRanking", "asc")
			.value();
	}

	@action
	async fetchPlayers() {
		const {data} = await this._jsonProvider.players();

		runInAction(() => {
			this._list = data;
		});
	}
}
